import {
  createEntityAdapter,
  createSlice,
  createSelector,
  EntityState,
} from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import { LegalVerification } from "../../../api/legalVerifications";
import {
  fetchLegalVerifications,
  upsertLegalVerifications,
} from "./legalVerificationThunk";
import { RequestStatus } from "./assetsSlice";

export const legalVerificationsAdapter = createEntityAdapter<
  LegalVerification,
  string
>({
  selectId: (verification) => verification.verification_id,
});

export interface LegalVerificationsState {
  legalVerifications: EntityState<LegalVerification, string>;
  verificationIdsByChargeId: Record<string, string[]>;
  status: RequestStatus;
  error?: string | null;
  caseId?: string;
}

const initialState: LegalVerificationsState = {
  legalVerifications: legalVerificationsAdapter.getInitialState(),
  verificationIdsByChargeId: {},
  status: "idle",
  error: null,
  caseId: undefined,
};

const appendVerificationToMap = (
  state: LegalVerificationsState,
  verification: LegalVerification,
) => {
  const { parent_entity_id, verification_id } = verification;
  if (!parent_entity_id) return;

  if (!state.verificationIdsByChargeId[parent_entity_id]) {
    state.verificationIdsByChargeId[parent_entity_id] = [];
  }
  if (
    !state.verificationIdsByChargeId[parent_entity_id].includes(verification_id)
  ) {
    state.verificationIdsByChargeId[parent_entity_id].push(verification_id);
  }
};

const legalVerificationsSlice = createSlice({
  name: "legalVerifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLegalVerifications.pending, (state) => {
        state.status = "loading";
        state.error = null;

        legalVerificationsAdapter.removeAll(state.legalVerifications);
        state.verificationIdsByChargeId = {};
      })

      .addCase(fetchLegalVerifications.fulfilled, (state, action) => {
        state.status = "success";
        state.caseId = action.meta.arg;

        legalVerificationsAdapter.setAll(
          state.legalVerifications,
          action.payload.verifications,
        );

        state.verificationIdsByChargeId = {};
        action.payload.verifications.forEach((v) => {
          appendVerificationToMap(state, v);
        });
      })

      .addCase(fetchLegalVerifications.rejected, (state) => {
        state.status = "failed";
        state.error = "Failed to fetch legal verifications";
      })

      .addCase(upsertLegalVerifications.fulfilled, (state, action) => {
        const verifications = action.meta.arg.request.verifications;
        legalVerificationsAdapter.upsertMany(
          state.legalVerifications,
          verifications,
        );
        verifications.forEach((v) => {
          appendVerificationToMap(state, v);
        });
      });
  },
});

export const legalVerificationsReducer = legalVerificationsSlice.reducer;

const legalVerificationsSelectors =
  legalVerificationsAdapter.getSelectors<RootState>(
    (state) => state.legalVerifications.legalVerifications,
  );

export const {
  selectAll: selectAllLegalVerifications,
  selectById: selectLegalVerificationById,
  selectEntities: selectLegalVerificationEntities,
  selectIds: selectLegalVerificationIds,
  selectTotal: selectLegalVerificationCount,
} = legalVerificationsSelectors;

export const selectLegalVerificationsStatus = (state: RootState) =>
  state.legalVerifications.status;

export const selectLegalVerificationsError = (state: RootState) =>
  state.legalVerifications.error;

export const selectLegalVerificationsByChargeIds = createSelector(
  [
    legalVerificationsSelectors.selectEntities,
    (state: RootState) => state.legalVerifications.verificationIdsByChargeId,
    (_state: RootState, chargeIds: string[]) => chargeIds,
  ],
  (verificationEntities, verificationIdsMap, chargeIds) => {
    const uniqueVerifications: LegalVerification[] = [];
    if (!chargeIds) return uniqueVerifications;

    const addedIds: string[] = [];

    chargeIds.forEach((chargeId) => {
      const mappedVerificationIds = verificationIdsMap[chargeId] || [];

      mappedVerificationIds.forEach((verificationId) => {
        if (!addedIds.includes(verificationId)) {
          addedIds.push(verificationId);

          const verification = verificationEntities[verificationId];
          if (verification) {
            uniqueVerifications.push(verification);
          }
        }
      });
    });

    return uniqueVerifications;
  },
);
